import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@eye-able/ui';
import { ZoomIn } from 'lucide-react';
import { cn } from '@helpers/cn';
import { DIALOG_SIZES, type PreviewDialogSize } from './dialog';
import { getPreviewFrameStyle, type PreviewSize } from './sizes';

/** Shared framing for a preview thumbnail: rounded, bordered, clipped. */
const FRAME_BASE_CLASS =
  'eye:relative eye:overflow-hidden eye:rounded-md eye:border eye:border-grey-100';

const DIALOG_SIZE_CLASSES: Record<PreviewDialogSize, string> = {
  [DIALOG_SIZES.default]: '',
  // True fullscreen: pin to the viewport edges so it escapes the overlay's
  // centring + padding and fills 100% (no rounding/border), scrolling inside.
  [DIALOG_SIZES.fullscreen]:
    'eye:fixed eye:inset-0 eye:h-dvh eye:w-screen eye:max-w-none! eye:rounded-none! eye:border-0! eye:overflow-y-auto',
};

export interface PreviewDialogProps {
  /** Accessible label for the activator / dialog. */
  ariaLabel: string;
  /** Dialog heading. */
  title: ReactNode;
  /** Preview footprint preset that drives the framed thumbnail's dimensions. */
  previewSize: PreviewSize;
  /** Fill a responsive grid cell while retaining the selected aspect ratio. */
  fluid?: boolean;
  /** Classes for the framed thumbnail (border, sizing, aspect ratio…). */
  frameClassName?: string;
  /** When false, only the thumbnail renders and clicking does nothing. */
  interactive?: boolean;
  /** Controlled open state. */
  isOpen: boolean;
  /** Controlled open-state setter. */
  onOpenChange: (open: boolean) => void;
  /** Preset dialog width. */
  size?: PreviewDialogSize;
  /** Extra classes for the dialog content wrapper. */
  contentClassName?: string;
  /** The thumbnail contents (image + optional overlay). */
  preview: ReactNode;
  /** The enlarged contents rendered inside the dialog. */
  children: ReactNode;
}

/** The zoom affordance shown in the bottom-right corner of a thumbnail. */
const ZoomBadge = () => (
  <span
    aria-hidden
    className='eye:absolute eye:bottom-0 eye:right-0 eye:z-1 eye:flex eye:size-[36px] eye:items-center eye:justify-center eye:rounded eye:bg-grey-20 eye:text-black eye:pointer-events-none'
  >
    <ZoomIn className='eye:size-24' />
  </span>
);

/**
 * Generic base for "thumbnail that opens an enlarged view in a dialog".
 *
 * It owns the framed, keyboard-accessible activator, the tooltip and the
 * {@link Dialog} shell — its footprint comes from a shared size preset, but it
 * knows nothing about images or bounding boxes. Compose it with a `preview`
 * node (the thumbnail) and `children` (the enlarged content). Open state is
 * controlled so callers can lazily load the full-size asset only while the
 * dialog is open.
 */
const PreviewDialog = ({
  ariaLabel,
  title,
  previewSize,
  fluid,
  frameClassName,
  interactive = true,
  isOpen,
  onOpenChange,
  size = DIALOG_SIZES.default,
  contentClassName,
  preview,
  children,
}: PreviewDialogProps) => {
  const frameStyle = getPreviewFrameStyle(previewSize, fluid);

  if (!interactive) {
    return (
      <div className={cn(FRAME_BASE_CLASS, frameClassName)} style={frameStyle}>
        {preview}
      </div>
    );
  }

  return (
    <>
      <button
        type='button'
        aria-label={ariaLabel}
        aria-haspopup='dialog'
        onClick={() => onOpenChange(true)}
        className={cn(
          FRAME_BASE_CLASS,
          'eye:block eye:w-full eye:cursor-pointer eye:p-0 eye:transition-[outline-color] eye:hover:outline-2 eye:hover:outline-offset-3 eye:hover:outline-black eye:focus-visible:outline-2 eye:focus-visible:outline-offset-3 eye:focus-visible:outline-black eye:active:outline-2 eye:active:outline-offset-3 eye:active:outline-black',
          frameClassName,
        )}
        style={frameStyle}
      >
        {preview}
        <ZoomBadge />
      </button>

      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          maxWidth='lg'
          className={cn(DIALOG_SIZE_CLASSES[size], contentClassName)}
        >
          <DialogTitle>{title}</DialogTitle>
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PreviewDialog;
